import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { SiteFooter } from "@/components/SiteFooter";
import { CalendarCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { useSiteSettings } from "@/lib/settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YEAR_LEVELS = ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12"];

// One question per step, in order.
const STEPS = [
  { key: "parent_name", type: "text", label: "First up — what's the parent's name?", placeholder: "Parent's full name", required: true },
  { key: "parent_phone", type: "text", label: "What's the best number to call you on?", placeholder: "04XX XXX XXX", required: true },
  { key: "parent_email", type: "email", label: "And your email address?", placeholder: "you@example.com", required: true },
  { key: "student_name", type: "text", label: "Who's the student?", placeholder: "Student's full name", hint: "Optional — you can tell us on the call." },
  { key: "year_level", type: "choice", label: "What year level is the student in?", options: YEAR_LEVELS, required: true },
  { key: "texts_studying", type: "text", label: "What texts are you studying this year?", placeholder: "e.g. Rainbow's End, Born a Crime…", hint: "Optional." },
  { key: "hopes", type: "textarea", label: "What are you hoping to get out of tutoring?", placeholder: "What would make this worth it for you?" },
  { key: "commit_year", type: "choice", label: "Are you willing to commit for the year?", options: ["Yes", "Not sure yet", "No"], required: true },
];

export default function EnrolPage() {
  const settings = useSiteSettings();
  const navigate = useNavigate();
  const term = settings.term_label;
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    parent_name: "", parent_phone: "", parent_email: "", student_name: "",
    texts_studying: "", hopes: "", commit_year: "", year_level: "",
  });

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  const value = form[current.key];

  const setValue = (v) => setForm((f) => ({ ...f, [current.key]: v }));

  const validateStep = () => {
    if (!current.required) return true;
    if (!String(form[current.key] || "").trim()) {
      toast.error("This one's required to continue.");
      return false;
    }
    if (current.type === "email" && !/^\S+@\S+\.\S+$/.test(form.parent_email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/trial-requests`, {
        ...form,
        subjects_interested: form.year_level ? `English (${form.year_level})` : "English",
      });
      navigate("/enrol/thank-you");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
      setLoading(false);
    }
  };

  const next = () => {
    if (!validateStep()) return;
    if (isLast) { submit(); return; }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const onKeyDown = (e) => {
    if (e.key === "Enter" && current.type !== "textarea") { e.preventDefault(); next(); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] flex flex-col" data-testid="enrol-page">
      <Seo
        title={`Enrol for ${term} — Small group English classes in Melbourne`}
        description={`Join the ${term} intake at Tutors Melbourne. Small group Year 7–12 English classes — hybrid model: online or in-person. Only 12 small groups available. We call you within 24 hours.`}
        path="/enrol"
      />
      <Navbar />

      <section className="hero-gradient dark:bg-transparent pt-28 pb-8 px-6">
        <div className="max-w-2xl mx-auto text-center animate-fade-in-up">
          <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full" data-testid="enrol-badge">
            <CalendarCheck className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" /> {term} intake — only 12 small groups
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-3">
            Enrol For {term}
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400 font-body">{settings.spots_note}</p>
        </div>
      </section>

      <section className="flex-1 px-6 pb-20">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-xs font-semibold uppercase tracking-wider text-brand-purple dark:text-brand-purple-light" data-testid="enrol-step-counter">
                Question {step + 1} of {STEPS.length}
              </span>
              <span className="font-body text-xs text-slate-400">{progress}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-gold transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
                data-testid="enrol-progress-bar"
              />
            </div>
          </div>

          <div
            key={step}
            className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-fade-in-up"
            data-testid="enrol-step-card"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white leading-snug mb-1" data-testid="enrol-question-label">
              {current.label}
              {current.required && <span className="text-brand-gold"> *</span>}
            </h2>
            {current.hint && <p className="font-body text-sm text-slate-400 mb-5">{current.hint}</p>}
            {!current.hint && <div className="mb-5" />}

            {(current.type === "text" || current.type === "email") && (
              <Input
                autoFocus
                type={current.type === "email" ? "email" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={current.placeholder}
                className="rounded-xl py-6 text-base"
                data-testid={`enrol-input-${current.key.replace(/_/g, "-")}`}
              />
            )}

            {current.type === "textarea" && (
              <Textarea
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={4}
                placeholder={current.placeholder}
                className="rounded-xl resize-none text-base"
                data-testid={`enrol-input-${current.key.replace(/_/g, "-")}`}
              />
            )}

            {current.type === "choice" && (
              <div className="flex flex-wrap gap-2.5" data-testid="enrol-choice-group">
                {current.options.map((opt) => (
                  <Button
                    key={opt}
                    type="button"
                    variant={value === opt ? "default" : "outline"}
                    onClick={() => setValue(opt)}
                    className={`rounded-full font-body px-7 py-6 ${
                      value === opt ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : ""
                    }`}
                    data-testid={`enrol-${current.key.replace(/_/g, "-")}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {opt}
                  </Button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 mt-8">
              {step > 0 ? (
                <Button variant="ghost" onClick={back} className="rounded-full font-body text-slate-500 hover:text-brand-purple" data-testid="enrol-back-btn">
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                </Button>
              ) : <span />}

              <Button
                onClick={next}
                disabled={loading}
                className="rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-bold px-8 py-6 shadow-md hover:shadow-lg disabled:opacity-60"
                data-testid={isLast ? "enrol-submit-btn" : "enrol-next-btn"}
              >
                {loading ? "Submitting…" : isLast ? `Enrol For ${term}` : "Next"}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </div>

          <p className="text-xs text-slate-400 dark:text-slate-500 font-body text-center mt-5" data-testid="enrol-disclaimer">
            We call every family personally before enrolling — so we only take students we know we can help.
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
